import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from './transactions.module.css';
import transferIcon from '../../assets/icons/transferIcon.svg';
import shopIcon from '../../assets/icons/store.svg';
import plussfu from '../../assets/icons/plussfu.svg';
import minussfu from '../../assets/icons/minussfu.svg';
import sfu from '../../assets/icons/sfu.svg';
import shopTransaction from '../../assets/icons/shopTransaction.png';
import closeIcon from '../../assets/icons/close.svg';
import { getMyTransactions, getMyWallet, transferPoints } from '../../api/profile';
import { searchUsers } from '../../api/search';
import {
  mapApiError,
  validateReceiverUserId,
  validateTransferAmount,
} from '../../utils/validators';
import { useAuth } from '../../context/AuthContext';
import { ModalPortal } from '../../components/ModalPortal/ModalPortal';
import { isAdminLikeRole } from '../../utils/roles';
import { WalletTabs } from '../Auction/common';
import { SeasonLink as Link } from '../../components/Seasons/SeasonLink';
import { useSeason } from '../../context/SeasonContext';

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 600);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return isMobile;
}

const FILTERS = [
  { id: 'all', label: 'Все' },
  { id: 'income', label: 'Пополнения' },
  { id: 'outcome', label: 'Списания' },
  { id: 'transfer', label: 'Переводы' }
];

function classifyTransaction(item) {
  const type = String(item.transaction_type || '').toUpperCase();
  if (type.includes('TRANSFER')) return 'transfer';
  if (Number(item.amount) > 0) return 'income';
  return 'outcome';
}

function formatDate(value) {
  try {
    return new Date(value).toLocaleString('ru-RU');
  } catch {
    return value;
  }
}

export function Transactions() {
  const { user } = useAuth();
  const isAdmin = isAdminLikeRole(user?.role);
  const { canEdit } = useSeason();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [items, setItems] = useState([]);
  const [walletBalance, setWalletBalance] = useState(0);
  const [activeFilter, setActiveFilter] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [recipient, setRecipient] = useState('');
  const [selectedRecipientId, setSelectedRecipientId] = useState(null);
  const [recipientSuggestions, setRecipientSuggestions] = useState([]);
  const [isRecipientLoading, setIsRecipientLoading] = useState(false);
  const [amount, setAmount] = useState('');
  const [comment, setComment] = useState('');
  const [hasMore, setHasMore] = useState(false);
  const [fieldError, setFieldError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [modalOffset, setModalOffset] = useState(0);
  const modalRef = useRef(null);
  const dragStartY = useRef(0);
  const dragStartOffset = useRef(0);
  const isMobile = useIsMobile();

  const loadData = useCallback(async () => {
    setError('');
    setLoading(true);
    try {
      const [wallet, transactions] = await Promise.all([
        getMyWallet(),
        getMyTransactions({ limit: 100, offset: 0 }),
      ]);
      setWalletBalance(wallet?.balance ?? 0);
      setItems(transactions || []);
      setHasMore(transactions?.length === 100);
    } catch (err) {
      setError(err.message || 'Не удалось загрузить транзакции.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleOpenTransferModal = () => {
    setIsModalOpen(true);
      setRecipient('');
      setSelectedRecipientId(null);
      setRecipientSuggestions([]);
      setAmount('');
      setComment('');
    setFieldError('');
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setRecipient('');
    setSelectedRecipientId(null);
    setRecipientSuggestions([]);
    setAmount('');
    setComment('');
    setFieldError('');
    setModalOffset(0);
  };

  const handleTouchStart = (e) => {
    dragStartY.current = e.touches[0].clientY;
    dragStartOffset.current = modalOffset;
  };

  const handleTouchMove = (e) => {
    const touchY = e.touches[0].clientY;
    const deltaY = touchY - dragStartY.current;
    if (deltaY > 0) {
      e.preventDefault();
      setModalOffset(Math.min(deltaY, 300));
    }
  };

  const handleTouchEnd = () => {
    if (modalOffset > 100) {
      handleCloseModal();
    } else {
      setModalOffset(0);
    }
  };

  useEffect(() => {
    if (!isModalOpen) return;
    const query = recipient.trim();
    if (query.length < 2 || /^\d+$/.test(query)) {
      setRecipientSuggestions([]);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setIsRecipientLoading(true);
      try {
        const response = await searchUsers(query, 8, 0);
        setRecipientSuggestions(response?.items || []);
      } catch {
        setRecipientSuggestions([]);
      } finally {
        setIsRecipientLoading(false);
      }
    }, 250);

    return () => clearTimeout(timeoutId);
  }, [isModalOpen, recipient]);

  const handleSubmitTransfer = async (e) => {
    e.preventDefault();
    setFieldError('');

    const parsedRecipient = selectedRecipientId || Number(recipient);
    const receiverError = validateReceiverUserId(parsedRecipient);
    const amountError = validateTransferAmount(amount);
    if (receiverError || amountError) {
      setFieldError(receiverError || amountError);
      return;
    }

    try {
      setIsSubmitting(true);
      await transferPoints(Number(parsedRecipient), Number(amount), comment.trim());
      await loadData();
      handleCloseModal();
    } catch (err) {
      setFieldError(mapApiError('transfer', err));
    } finally {
      setIsSubmitting(false);
    }
  };

  const filtered = useMemo(() => {
    return items.filter((item) => (
      activeFilter === 'all' || classifyTransaction(item) === activeFilter
    ));
  }, [items, activeFilter]);

  const last7DaysIncome = useMemo(() => {
    const now = Date.now();
    const sevenDaysAgo = now - 7 * 24 * 60 * 60 * 1000;
    return items
      .filter((item) => {
        const itemDate = new Date(item.created_at).getTime();
        return Number(item.amount) > 0 && itemDate >= sevenDaysAgo;
      })
      .reduce((sum, item) => sum + Number(item.amount || 0), 0);
  }, [items]);

  return (
    <section className={styles.page}>
      <WalletTabs />
      <h1 className={styles.title}>Кошелек</h1>

      <div className={styles.balanceBlock}>
        <div className={styles.balanceLeft}>
          <div className={styles.balanceLabel}>Баланс юфунтов</div>
          <div className={styles.balanceValue}>
            <span>{walletBalance.toLocaleString('ru-RU')}</span>
            <img className={styles.balanceIcon} src={sfu} alt="Баллы" />
          </div>
          <div className={styles.balanceGrowth}>
            {(
              <span className={styles.growthWithIcon}>
                <img className={styles.inlineCoinIcon} src={sfu} alt="" />
                +{last7DaysIncome} за последние 7 дней
              </span>
            )}
          </div>
        </div>
        <div className={styles.balanceRight}>
          {isAdmin && canEdit && <button className={styles.actionButton} onClick={handleOpenTransferModal} type="button">
            <img src={transferIcon} alt="" />
            <span style={{ fontFamily: 'Onset' }}>Перевести</span>
          </button>}
          <Link
            className={styles.actionButtonSecondary}
            to="/transactions/auction"
          >
            <img src={shopIcon} alt="" />
            <span>Аукцион мерча</span>
          </Link>
        </div>
      </div>

      {error ? <p className={styles.error}>{error}</p> : null}
      {loading ? <p className={styles.muted}>Загружаем историю...</p> : null}

      {!loading && !filtered.length ? <p className={styles.muted}>Ничего не найдено.</p> : null}

      <div className={styles.contentGrid}>
        <div className={styles.transactionsColumn}>
          <div className={styles.filters}>
            <p className={styles.filtersLabel}>История транзакций</p>
            {FILTERS.map((filter) => (
              <button
                key={filter.id}
                type="button"
                className={`${styles.filterBtn} ${activeFilter === filter.id ? styles.filterBtnActive : ''}`}
                onClick={() => setActiveFilter(filter.id)}
              >
                {filter.label}
              </button>
            ))}
          </div>
          <div className={styles.list}>
            {filtered.map((item) => {
              const amountValue = Number(item.amount || 0);
              const isIncome = amountValue > 0;
              const isOutcome = amountValue < 0;

              return (
                <article className={styles.card} key={item.transaction_id}>
                  <div className={styles.cardTop}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {isIncome ? (
                        <img src={plussfu} alt="" className={styles.cardIcon} />
                      ) : isOutcome ? (
                        <img src={minussfu} alt="" className={styles.cardIcon} />
                      ) : (
                        <img src={shopTransaction} alt="" className={styles.cardIcon} />
                      )}
                      <span className={styles.type}>{{AUCTION_BID:'Ставка на аукционе',AUCTION_REFUND:'Возврат за отменённый лот',AUCTION_ADJUSTMENT:'Корректировка аукциона',ADMIN_DEPOSIT:'Начисление',ADMIN_DEDUCT:'Списание',TRANSFER_IN:'Входящий перевод',TRANSFER_OUT:'Исходящий перевод'}[item.transaction_type] || item.transaction_type}</span>
                    </div>
                    <span className={isIncome ? styles.amountPlus : styles.amountMinus}>
                      <span className={styles.amountWithIcon}>
                        {isIncome ? `+${amountValue}` : amountValue}
                        <img className={styles.inlineCoinIcon} src={sfu} alt="" />
                      </span>
                    </span>
                  </div>

                  <div className={styles.meta}>
                    <span>{item.comment || 'Без комментария'}</span>
                    <span>{formatDate(item.created_at)}</span>
                  </div>
                </article>
              );
            })}
          </div>
          {hasMore && <button type="button" className={styles.filterBtn} disabled={loading} onClick={async () => {
            setLoading(true);
            try { const next = await getMyTransactions({ limit:100,offset:items.length }); setItems((v) => [...v,...next]); setHasMore(next.length === 100); }
            catch (e) { setError(e.message); } finally { setLoading(false); }
          }}>Загрузить ещё</button>}
        </div>

        <div className={styles.productsColumn}>
          <h2 className={styles.productsTitle}>Аукцион мерча</h2>
          <div className={styles.storeSoonBox}>
            <p>Выбирайте лоты и тратьте заработанные юфунты на мерч. Ваши ставки и результаты — в разделе «Мои лоты».</p>
            <Link className={styles.actionButtonSecondary} to="/transactions/auction">Перейти к лотам →</Link>
          </div>
        </div>
      </div>

      {isModalOpen && (
        <ModalPortal onClose={handleCloseModal} isMobile={isMobile}>
          <div
            className={styles.modalContentWrapper}
            ref={modalRef}
            style={{ transform: `translateY(${modalOffset}px)` }}
          >
            <div
              className={styles.modalDragHandleArea}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            />
            <div
              className={styles.modalDragHandle}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            />
            <button className={styles.modalClose} onClick={handleCloseModal} type="button">
              <img src={closeIcon} alt="Закрыть" />
            </button>
            <h2 className={styles.modalTitle}>Перевод монет</h2>
            <form className={styles.modalForm} onSubmit={handleSubmitTransfer}>
              <div className={styles.formGroup}>
                <label className={styles.formLabel} htmlFor="recipient">
                  Получатель (ID)
                </label>
                <input
                  className={styles.formInput}
                  id="recipient"
                  type="text"
                  value={recipient}
                  onChange={(e) => {
                    setRecipient(e.target.value);
                    setSelectedRecipientId(null);
                    setFieldError('');
                  }}
                  placeholder="ID, логин или ФИО"
                  autoFocus
                />
                {isRecipientLoading ? <p className={styles.formHint}>Ищем пользователей...</p> : null}
                {!isRecipientLoading && recipientSuggestions.length ? (
                  <div className={styles.suggestions}>
                    {recipientSuggestions.map((candidate) => (
                      <button
                        key={candidate.user_id}
                        className={styles.suggestionBtn}
                        type="button"
                        onClick={() => {
                          const fullName = `${candidate.last_name} ${candidate.first_name}`.trim();
                          setRecipient(fullName || candidate.login);
                          setSelectedRecipientId(candidate.user_id);
                          setRecipientSuggestions([]);
                        }}
                      >
                        {candidate.last_name} {candidate.first_name} (@{candidate.login}) · ID {candidate.user_id}
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel} htmlFor="amount">
                  Количество монет
                </label>
                <input
                  className={styles.formInput}
                  id="amount"
                  type="number"
                  value={amount}
                  onChange={(e) => {
                    setAmount(e.target.value);
                    setFieldError('');
                  }}
                  placeholder="Например: 500"
                  min="1"
                  step="1"
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel} htmlFor="comment">
                  Комментарий (необязательно)
                </label>
                <input
                  className={styles.formInput}
                  id="comment"
                  type="text"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Комментарий к переводу"
                />
              </div>
              {fieldError && <p className={styles.formError}>{fieldError}</p>}
              <div className={styles.modalButtons}>
                <button
                  className={styles.modalButtonCancel}
                  type="button"
                  onClick={handleCloseModal}
                >
                  Отмена
                </button>
                <button
                  className={styles.modalButtonSubmit}
                  type="submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Отправка...' : 'Отправить'}
                </button>
              </div>
            </form>
          </div>
        </ModalPortal>
      )}
    </section>
  );
}
